// client/src/components/UserProfile.js

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AppBarHeader from './AppBarHeader';
import { jwtDecode } from 'jwt-decode';
import {
  Box, Typography, Button, Alert, Divider, Chip,
  Paper, Avatar, Stack, Skeleton,
} from '@mui/material';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import ChatIcon from '@mui/icons-material/Chat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import axiosInstance from '../axiosInstance';
import FollowButton from './social/FollowButton';
import ReputationPanel from './reputation/ReputationPanel';
import ActivityFeed from './social/ActivityFeed';

function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const { featureFlags } = useFeatureFlags();
  const [chatPresence, setChatPresence] = useState(null);

  const token = localStorage.getItem('token');
  const userDecoded = token ? jwtDecode(token) : null;
  const userId = userDecoded ? userDecoded.id : null;
  const isAdmin = userDecoded ? userDecoded.isAdmin : false;
  const isOwnProfile = userId && userId === id;

  useEffect(() => {
    if (!featureFlags?.enableChatRooms || !id) return;
    axiosInstance.get('/chat/online')
      .then(res => {
        const entry = (res.data || []).find(u => String(u.userId) === String(id));
        setChatPresence(entry || null);
      })
      .catch(() => {});
  }, [id, featureFlags?.enableChatRooms]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/users/${id}/profile`);
        if (response.data.profileImage) {
          response.data.profileImage = axiosInstance.getImageUrl(response.data.profileImage);
        }
        setUserData(response.data);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('An error occurred while fetching this profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [id]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const formatJoinDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  };

  if (error) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 4, textAlign: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <div>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Box sx={{ maxWidth: 700, mx: 'auto', px: 2, py: 4 }}>

        {/* Profile header card */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          {loading ? (
            <Stack spacing={2} alignItems="center">
              <Skeleton variant="circular" width={120} height={120} />
              <Skeleton width={200} height={32} />
              <Skeleton width={300} height={20} />
            </Stack>
          ) : (
            <Stack spacing={2} alignItems="center">
              <Avatar
                alt={userData.username}
                src={userData.profileImage || undefined}
                sx={{ width: 120, height: 120, fontSize: 48, bgcolor: 'primary.main' }}
              >
                {userData.username?.[0]?.toUpperCase()}
              </Avatar>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={600}>{userData.username}</Typography>

                {userData.bio && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 420, mx: 'auto' }}>
                    {userData.bio}
                  </Typography>
                )}

                {userData.createdAt && (
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.disabled">
                      Member since {formatJoinDate(userData.createdAt)}
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* Follower / following counts */}
              <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem />}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={600}>{userData.followerCount ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Followers</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={600}>{userData.followingCount ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Following</Typography>
                </Box>
              </Stack>

              {/* Reputation */}
              <ReputationPanel userId={id} />

              {/* Actions */}
              {!isOwnProfile && (
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                  {featureFlags?.enableFollowSystem && (
                    <FollowButton targetUserId={id} />
                  )}
                  {chatPresence?.room && (
                    <Chip
                      icon={<ChatIcon />}
                      label={`In #${chatPresence.room}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      component={Link}
                      to={`/chat?room=${chatPresence.room}`}
                      clickable
                    />
                  )}
                </Stack>
              )}

              {isOwnProfile && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate('/profile')}
                >
                  Edit Profile
                </Button>
              )}
            </Stack>
          )}
        </Paper>

        {/* Collection / wishlist links */}
        {!loading && (
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<CollectionsBookmarkIcon />}
                onClick={() => navigate(`/collections/${id}`)}
              >
                View Collection
              </Button>
              <Button
                variant="outlined"
                startIcon={<FavoriteIcon />}
                onClick={() => navigate(`/wishlists/${id}`)}
              >
                View Wishlist
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Activity feed (other users only) */}
        {!loading && !isOwnProfile && userId && (
          <ActivityFeed userId={id} currentUserId={userId} />
        )}
      </Box>
    </div>
  );
}

export default UserProfile;
